import React from "react";
import styled from "styled-components";

import { Step, StepLabel, Stepper } from "@mui/material";
import StepConnector, { stepConnectorClasses } from "@mui/material/StepConnector";

import Button from "@ingka/button";
import { colourSemanticInformative } from "@ingka/variables/colours-css";
import arrowRight from "@ingka/ssr-icon/paths/arrow-right";
import arrowLeft from "@ingka/ssr-icon/paths/arrow-left";
import Toast from "@ingka/toast";

import { CreateNewApplicationSteps } from "./CreateNewApplication";
import { ApplicationValidation, Valid, Value } from "../../utils/types";
import { SrOnly } from "../styles";

const StyledConnector = styled(StepConnector)(({ theme }) => ({
	[`&.${stepConnectorClasses.alternativeLabel}`]: {
		top: 10,
		left: "calc(-50% + 16px)",
		right: "calc(50% + 16px)",
	},
	[`&.${stepConnectorClasses.active}`]: {
		[`& .${stepConnectorClasses.line}`]: {
			borderColor: colourSemanticInformative,
		},
	},
	[`&.${stepConnectorClasses.completed}`]: {
		[`& .${stepConnectorClasses.line}`]: {
			borderColor: colourSemanticInformative,
		},
	},
	[`& .${stepConnectorClasses.line}`]: {
		borderTopWidth: 3,
	},
}));

const ContentLayout = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-direction: column;
	gap: 1rem;
	margin-top: 1rem;
	padding: 1rem;
	position: relative;
`;

const BackButton = styled(Button)`
	position: absolute;
	bottom: 0;
	left: 0;
`;
const SkipButton = styled(Button)`
	position: absolute;
	bottom: 0;
	right: 0;
`;

export const VisuallyHidden = styled.span`
  border: 0;
  clip: rect(1px, 1px, 1px, 1px);
  -webkit-clip-path: inset(50%)
          clip-path: inset(50%);
  height: 1px;
  width: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute !important;
  word-wrap: normal !important;
`;

interface CreateNewApplicationLayoutProps {
	steps: CreateNewApplicationSteps[];
	activeStep: number;
	onClickNext: () => void;
	isFinalStep: boolean;
	onBack: () => void;
	userLoading: boolean;
	loadingCreateNewApp: boolean;
	toast: string;
	setToast: (str: string) => void;
	value: Value;
	valid: Valid;
	validation: ApplicationValidation;
	shouldValidate: Valid;
	handleChange: (val: string | boolean | string[], name: string) => void;
}

const Layout: React.FC<CreateNewApplicationLayoutProps> = ({
	steps,
	activeStep,
	onClickNext,
	isFinalStep,
	onBack,
	userLoading,
	loadingCreateNewApp,
	toast,
	setToast,
	value,
	valid,
	validation,
	shouldValidate,
	handleChange,
}) => {
	const getStepA11yLabel = (stepIndex: number, activeStep: number) => {
		const step = stepIndex + 1;
		if (stepIndex === activeStep) {
			return `Current step ${step}`;
		}
		if (stepIndex < activeStep) {
			return `Completed step ${step}`;
		}
		return `step ${step}`;
	};
	const { Element } = steps[activeStep];

	return (
		<>
			<Stepper
				sx={{
					"& .MuiStepLabel-iconContainer .Mui-active": {
						color: colourSemanticInformative,
					},
					"& .MuiStepLabel-iconContainer .Mui-completed": {
						color: colourSemanticInformative,
					},
				}}
				connector={<StyledConnector />}
				activeStep={activeStep}
				alternativeLabel
			>
				{steps.map(({ index, id, label }) => (
					<Step
						key={id}
						disabled={loadingCreateNewApp || userLoading}
						{...(activeStep === index && { "aria-live": "polite" })}
					>
						<StepLabel
							sx={{
								"& .MuiStepLabel-labelContainer .Mui-active": { fontWeight: 700 },
							}}
						>
							<VisuallyHidden>{getStepA11yLabel(index, activeStep)}</VisuallyHidden>
							{<span>{label}</span>}
						</StepLabel>
					</Step>
				))}
			</Stepper>
			<ContentLayout>
				<>
					<SrOnly id="loading-message" role="status" aria-live="polite" aria-atomic="true">
						{loadingCreateNewApp && "Loading"}
					</SrOnly>
					{Element && (
						<Element
							value={value}
							valid={valid}
							shouldValidate={shouldValidate}
							validation={validation}
							handleChange={handleChange}
							activeStep={activeStep}
						/>
					)}
					<Button
						type="emphasised"
						text={isFinalStep ? "Send Request" : "Next"}
						ssrIcon={arrowRight}
						iconPosition="trailing"
						onClick={onClickNext}
						disabled={!steps[activeStep].isValid}
						loading={userLoading || loadingCreateNewApp}
					/>
				</>
				{steps[activeStep].skipable && (
					<SkipButton
						text="Skip"
						ssrIcon={arrowRight}
						iconPosition="trailing"
						type="plain"
						disabled={loadingCreateNewApp || userLoading}
						onClick={onClickNext}
					/>
				)}

				{activeStep !== 0 && (
					<BackButton
						text="Back"
						type="plain"
						ssrIcon={arrowLeft}
						onClick={onBack}
						disabled={loadingCreateNewApp || userLoading}
					/>
				)}
			</ContentLayout>
			<Toast
				text={toast}
				isOpen={!!toast}
				ariaLabelCloseBtn="Dismiss notification"
				onCloseRequest={() => setToast("")}
			/>
		</>
	);
};

export default Layout;
